import {
  ApiDataProcessorConfiguration,
  ApiDataProcessorType,
  ApiReturnDevice,
  ApiReturnDeviceSite,
  ApiReturnDeviceTypeMeasureType,
  ApiReturnMeasurePoint,
  ApiReturnMeasureType,
} from '@highimpact-frontend/api-types';
import Stack from '@mui/material/Stack';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import DataProcessorConfigurationService from '../../api/services/dataprocessors/DataProcessorConfiguration/DataProcessorConfigurationService';
import {
  defaultValuesFromConfig,
  FieldType,
  fieldTypeToConfig,
} from '../../utils/formGenerator/DataProcessorConfigToFields';
import FormGenerator from '../../utils/formGenerator/FormGenerator';
import { FormElementConfigW } from '../../utils/formGenerator/Types';
import { useSubmit } from '../../utils/hooks/useSubmit';
import CustomizedDialog from '../utils/MUISpecifics/dialog/CustumizedDialog';
import DropDownItemSelector from '../utils/MUISpecifics/dropDownItemSelector/DropDownItemSelector';
import InputMultiType from '../utils/MUISpecifics/inputMultiType/InputMultiType';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

/**
 * Get all MeasureTypes linked to a DeviceSite
 */
function getMeasureTypesForDeviceSite(
  device_site: ApiReturnDeviceSite,
  devices: ApiReturnDevice[],
  device_type_measure_types: ApiReturnDeviceTypeMeasureType[],
  measure_types: ApiReturnMeasureType[]
): ApiReturnMeasureType[] {
  const site_devices = devices.filter((d) => device_site.device_id === d.id);
  const device_type_ids = site_devices.map((d) => d.device_type_id);
  const measure_type_ids = device_type_measure_types
    .filter((dtmt) => device_type_ids.includes(dtmt.device_type_id))
    .map((dtmt) => dtmt.measure_type_id);
  const unique_measure_type_ids = [...new Set(measure_type_ids)];
  return measure_types.filter((mt) => unique_measure_type_ids.includes(mt.id));
}

function getMeasureTypesForDeviceSiteId(
  device_site_id: number,
  device_sites: ApiReturnDeviceSite[],
  devices: ApiReturnDevice[],
  device_type_measure_types: ApiReturnDeviceTypeMeasureType[],
  measure_types: ApiReturnMeasureType[]
): ApiReturnMeasureType[] {
  const device_site = device_sites.find(({ id }) => id === device_site_id);
  if (!device_site) return [];
  return getMeasureTypesForDeviceSite(
    device_site,
    devices,
    device_type_measure_types,
    measure_types
  );
}

/**
 * Helper for translate labels for the form elements, that came from backend
 */
const translateFormConfig = <T extends object>(
  config: FormElementConfigW<T>[],
  t: TFunction
): FormElementConfigW<T>[] =>
  config.map((conf) => ({
    ...conf,
    label: conf.label ? t(conf.label) : conf.label,
    placeHolder: conf.placeHolder ? t(conf.placeHolder) : conf.placeHolder,
    selectionEntries: conf.selectionEntries?.map((entry) => ({
      ...entry,
      name: t(entry.name),
    })),
  }));


type SpecificConfig = NonNullable<
  ApiDataProcessorConfiguration['specific_configuration']
>;

/**
 * Tagged union props
 */
interface CommonProps {
  isOpen: boolean;
  closeModal: () => void;
  monitoringData: ApiDataProcessorType[];
  stations: ApiReturnMeasurePoint[];
  devices: ApiReturnDevice[];
  deviceTypeMeasureTypes: ApiReturnDeviceTypeMeasureType[];
  measureTypes: ApiReturnMeasureType[];
  projectId: number;
  siteId: number;
}

interface CreateAlertConfigProps extends CommonProps {
  mode: 'create';
}

interface UpdateAlertConfigProps extends CommonProps {
  mode: 'update';
  configId: number;
  config: ApiDataProcessorConfiguration;
}

type MonitoringProps = CreateAlertConfigProps | UpdateAlertConfigProps;

function CreateAlertConfiguration(
  props: MonitoringProps,
  _ref: any
): JSX.Element {
  const {
    mode,
    isOpen,
    closeModal,
    monitoringData,
    stations,
    devices,
    deviceTypeMeasureTypes,
    measureTypes,
    projectId,
    siteId,
  } = props;

  const { submit } = useSubmit();
  const { t } = useTranslation(['alerts', 'commons']);

  const [formConfig, setFormConfig] = useState<
    FormElementConfigW<Record<string, any>>[]
  >([]);
  const [defaultValues, setDefaultValues] = useState<SpecificConfig>(
    (mode === 'update' &&
      (props.config.specific_configuration as SpecificConfig)) ||
    {}
  );
  // In update mode, we want the initial selection to match the device_site
  const [measureTypesForStation, setMeasureTypesForStation] = useState<
    ApiReturnMeasureType[]
  >(
    mode === 'update'
      ? getMeasureTypesForDeviceSiteId(
        props.config.device_site_id,
        stations
          .map((s) => s.device_site)
          .filter((ds): ds is ApiReturnDeviceSite => Boolean(ds)),
        devices,
        deviceTypeMeasureTypes,
        measureTypes
      )
      : []
  );

  type CurrentSelection = {
    dataprocessor_type_id: number | null;
    station_id: number | null;
    measure_type_id: number | null;
    start_temporization: number | null;
    end_temporization: number | null;
  };

  const initialValues = {
    dataprocessor_type_id:
      mode === 'update' ? props.config.dataprocessor_type_id : null,
    station_id: mode === 'update' ? props.config.device_site_id : null,
    measure_type_id: mode === 'update' ? props.config.measure_type_id : null,
    start_temporization: mode === 'update' ? props.config.start_temporization : null,
    end_temporization: mode === 'update' ? props.config.end_temporization : null,
  };

  const dataRef = useRef<CurrentSelection>(initialValues);

  const updateStation = (stationMeasurePointId: number | null) => {
    const match = stations.find(({ id }) => id === stationMeasurePointId);
    if (match?.device_site) {
      dataRef.current.station_id = match.device_site.id;
      setMeasureTypesForStation(
        getMeasureTypesForDeviceSite(
          match.device_site,
          devices,
          deviceTypeMeasureTypes,
          measureTypes
        )
      );
    } else {
      dataRef.current.station_id = null;
      setMeasureTypesForStation([]);
    }
  };

  const updateDataProcessorType = (id: number) => {
    const match = monitoringData.find((md) => md.id === id);

    if (match) {
      dataRef.current.dataprocessor_type_id = match.id!;
      const rawConfig = fieldTypeToConfig(match.configuration);
      setFormConfig(translateFormConfig(rawConfig, t));

      const defaults = defaultValuesFromConfig<SpecificConfig>(
        Object.values(match.configuration) as FieldType[]
      );

      setDefaultValues(defaults as SpecificConfig);
    } else {
      dataRef.current.dataprocessor_type_id = null;
      setFormConfig([]);
      setDefaultValues({});
    }
  };

  const updateMeasureType = (id: number) => {
    dataRef.current.measure_type_id = id;
  };

  const updateStartTemporization = (value: number) => {
    dataRef.current.start_temporization = value;
  };

  const updateEndTemporization = (value: number) => {
    dataRef.current.end_temporization = value;
  };

  useEffect(() => {
    if (mode === 'update') {
      // Initialize state in update mode
      const { config } = props;
      const dpType = monitoringData.find(
        (md) => md.id === config.dataprocessor_type_id
      );
      if (dpType) {
        const rawConfig = fieldTypeToConfig(dpType.configuration);
        setFormConfig(translateFormConfig(rawConfig, t));
      } else {
        setFormConfig([]);
      }
    } else {
      // Reset for create mode
      setFormConfig([]);
      setDefaultValues({});
      setMeasureTypesForStation([]);
      dataRef.current = {
        dataprocessor_type_id: null,
        station_id: null,
        measure_type_id: null,
        start_temporization: null,
        end_temporization: null,
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, isOpen]);

  const onSubmit = async (data?: object) => {
    const specific =
      data as ApiDataProcessorConfiguration['specific_configuration'];

    if (dataRef.current.station_id === null) {
      throw new Error(t('Veuillez sélectionner un capteur.'));
    }
    if (dataRef.current.dataprocessor_type_id === null) {
      throw new Error(t('Veuillez sélectionner un type de configuration.'));
    }
    if (dataRef.current.measure_type_id === null) {
      throw new Error(t('Veuillez sélectionner un type de mesure.'));
    }
    if (dataRef.current.start_temporization === null) {
      throw new Error(t('Veuillez sélectionner une temporisation d\'entrée.'));
    }
    if (dataRef.current.end_temporization === null) {
      throw new Error(t('Veuillez sélectionner une temporisation de sortie.'));
    }

    const alertConfigToPersist: ApiDataProcessorConfiguration = {
      device_site_id: dataRef.current.station_id,
      dataprocessor_type_id: dataRef.current.dataprocessor_type_id,
      measure_type_id: dataRef.current.measure_type_id,
      specific_configuration: specific,
      start_temporization: dataRef.current.start_temporization,
      end_temporization: dataRef.current.end_temporization,
      keep_lower_alert: true
    };

    if (mode === 'create') {
      await submit({
        callbacks: [
          async () =>
            await DataProcessorConfigurationService.create(
              projectId,
              siteId,
              alertConfigToPersist
            ),
        ],
        errorMessages: {
          anyError: t('Une erreur est survenue lors de la création de la configuration.'),
          unicityError: t('Une configuration avec ce type et cette mesure existe déjà.'),
        },
        successMessage: t('La configuration a été créée avec succès.'),
      });
    } else {
      const { configId } = props;
      await submit({
        callbacks: [
          async () =>
            await DataProcessorConfigurationService.update(
              projectId,
              siteId,
              configId,
              alertConfigToPersist
            ),
        ],
        errorMessages: {
          anyError: t('Une erreur est survenue lors de la mise à jour de la configuration.'),
          unicityError: t('Une configuration avec ce type et cette mesure existe déjà.'),
        },
        successMessage: t('La configuration a été mise à jour avec succès.'),
      });
    }
  };

  const onFormGeneratorSubmit = async (data: object) => {
    try {
      await onSubmit(data);
      closeModal();
    } catch {
      // keep dialog open on error
    }
  };

  const isUpdate = mode === 'update';
  const title = isUpdate
    ? t('Mettre à jour la configuration')
    : t('Nouvelle configuration');

  // In update mode, lock selectors to avoid changing identity fields
  const disableIdentitySelectors = isUpdate;

  const translatedMonitoringData = useMemo(
    () =>
      monitoringData.map((md) => ({
        ...md,
        name: t(md.name),
      })),
    [monitoringData, t]
  );

  return (
    <div
      style={{
        padding: '3em',
        border: '1px solid #ccc',
        borderRadius: '0.3em',
      }}
    >
      <CustomizedDialog onCancel={closeModal} open={isOpen} title={title}>
        <Stack spacing={2}>
          <DropDownItemSelector
            onSelect={updateStation}
            values={stations}
            defaultValue={initialValues.station_id}
            disabled={disableIdentitySelectors}
            name={'station'}
            label={t('Station de mesure')}
          />
          <DropDownItemSelector
            onSelect={(value) => value && updateDataProcessorType(value)}
            values={translatedMonitoringData as (ApiDataProcessorType & { id: number })[]}
            defaultValue={initialValues.dataprocessor_type_id}
            disabled={disableIdentitySelectors}
            name={'config_type'}
            label={t("Type d'alerte")}
          />
          <DropDownItemSelector
            onSelect={(value) => value && updateMeasureType(value)}
            values={measureTypesForStation}
            defaultValue={initialValues.measure_type_id}
            disabled={disableIdentitySelectors}
            name={'measure_type'}
            label={t('Choisir un type de mesure')}
          />
          <InputMultiType
            type={'number'}
            onChange={(value) => value && updateStartTemporization(Number(value))}
            name={'Temporisation d\'entrée'}
            label={t('Temporisation d\'entrée (secondes)')}
            defaultValue={initialValues.start_temporization ?
              Number(initialValues.start_temporization) : ''
            }
          />
          <InputMultiType
            type={'number'}
            onChange={(value) => value && updateEndTemporization(Number(value))}
            name={'Temporisation de sortie'}
            label={t('Temporisation de sortie (secondes)')}
            defaultValue={initialValues.end_temporization ?
              Number(initialValues.end_temporization) : ''
            }
          />
          <FormGenerator
            defaultValues={defaultValues}
            onSubmit={onFormGeneratorSubmit}
            config={formConfig}
            withButton={true}
            buttonLabel={isUpdate ? t('Mettre à jour') : t('Créer')}
          />
        </Stack>
      </CustomizedDialog>
    </div>
  );
}

export default forwardRef(CreateAlertConfiguration);